import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import CategoryPill from "@/components/base/CategoryPill";
import TopicCard from "@/components/base/TopicCard";
import Reveal from "@/components/base/Reveal";
import { categories } from "@/mocks/categories";
import { topics } from "@/mocks/topics";
import { formatHeat } from "@/utils/format";

export default function TopicGrid() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") ?? "all";

  const filtered = useMemo(() => {
    const list = activeCategory === "all" ? topics : topics.filter((topic) => topic.category === activeCategory);
    return [...list].sort((a, b) => b.heat - a.heat);
  }, [activeCategory]);

  const handleSelect = (key: string) => {
    const next = new URLSearchParams(searchParams);
    if (key === "all") {
      next.delete("category");
    } else {
      next.set("category", key);
    }
    setSearchParams(next, { replace: true });
  };

  const hotRank = [...topics].sort((a, b) => b.heat - a.heat).slice(0, 5);

  return (
    <div className="mx-auto grid w-full grid-cols-1 gap-10 px-4 py-14 md:px-6 md:py-16 lg:grid-cols-[minmax(0,1fr)_300px]">
      <div>
        <div className="flex flex-wrap gap-2">
          <CategoryPill
            label="全部"
            icon="ri-apps-2-line"
            active={activeCategory === "all"}
            onClick={() => handleSelect("all")}
          />
          {categories.map((category) => (
            <CategoryPill
              key={category.key}
              label={category.name}
              icon={category.icon}
              active={activeCategory === category.key}
              onClick={() => handleSelect(category.key)}
            />
          ))}
        </div>

        <p className="mt-5 text-sm text-foreground-500" role="status">
          共找到 <strong className="font-medium text-foreground-900">{filtered.length}</strong> 条热梗整理
        </p>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {filtered.map((topic, index) => (
              <Reveal key={topic.id} delay={index * 50}>
                <TopicCard topic={topic} />
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-500">
              <i className="ri-search-eye-line" aria-hidden="true"></i>
            </span>
            <p className="mt-4 text-base text-foreground-700">这个分区还没有整理好的内容</p>
            <button
              type="button"
              onClick={() => handleSelect("all")}
              className="mt-4 cursor-pointer rounded-md bg-primary-500 px-4 py-2.5 font-label text-sm text-background-50 hover:bg-primary-600"
            >
              看看全部热梗
            </button>
          </div>
        )}
      </div>

      <aside className="lg:sticky lg:top-28 lg:self-start" aria-label="侧边栏">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
            <i className="ri-fire-fill text-primary-500" aria-hidden="true"></i>
            热度榜 TOP 5
          </h2>
          <ol className="mt-4 space-y-3">
            {hotRank.map((topic, index) => (
              <li key={topic.id} className="flex items-start gap-3">
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-heading text-sm ${
                    index === 0 ? "bg-accent-500 text-foreground-950" : "bg-background-100 text-foreground-600"
                  }`}
                >
                  {index + 1}
                </span>
                <div className="min-w-0">
                  <Link
                    to={`/topics/${topic.id}`}
                    className="line-clamp-2 text-sm text-foreground-800 transition-colors hover:text-primary-700"
                  >
                    {topic.title}
                  </Link>
                  <span className="mt-0.5 block text-xs text-foreground-500">{formatHeat(topic.heat)}</span>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-5 rounded-lg border border-secondary-200 bg-secondary-50 p-5">
          <h2 className="flex items-center gap-2 font-heading text-lg text-secondary-800">
            <i className="ri-lightbulb-line" aria-hidden="true"></i>
            怎么看懂一个梗
          </h2>
          <ul className="mt-4 space-y-2.5 text-sm leading-relaxed text-foreground-600">
            <li>先看专题的「来龙去脉」，了解它为什么火。</li>
            <li>再看「衍生玩法」，判断它有没有长出固定用法。</li>
            <li>最后翻表情包与神评，就能顺畅接梗了。</li>
          </ul>
        </div>
      </aside>
    </div>
  );
}